import { Button } from '@heidi/design-system/button';
import { TypographyCaption } from '@heidi/design-system/typography';
import { QueryClientContext } from '@tanstack/react-query';
import { Loader2, Square } from 'lucide-react';
import { useContext, useState, type JSX, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import type { PublicRunSnapshot } from '../../../contracts';
import type { ComputerUseRunControlPort } from '../../../contracts/projects/computer-use-run-control.types';
import type { DelegateTraceEntry } from '../../../data';
import { useSubAgentComputerUseProgress } from '../../../data';
import { agentHarnessClient } from '../../agent-harness/client';
import { useQueryRun } from '../../agent-harness/hooks';
import { streamHeaders } from '../../agent-harness/stream/stream-http';
import { ComputerUseMarkdown } from './ComputerUseTaskBlock.status';
import { ComputerUseWorkingBlock } from './ComputerUseWorkingBlock';

interface RunningBodyProps {
  childAgentRunId: string | undefined;
  awaitingApproval: boolean;
  trace: DelegateTraceEntry[] | undefined;
  elapsedSeconds: number | null;
  /** Skip the read-only live view (a sibling clarification card shows its
   * own interactive one). */
  hideLiveView?: boolean;
  /** Stops the run this task is queued behind, and reads the queue. */
  runControl: ComputerUseRunControlPort;
  /** The cloud resident's read-only live screen for this run. */
  renderLiveView: (childAgentRunId: string | undefined) => ReactNode;
}

const OCCUPYING_GOAL_MAX_CHARS = 140;

// A heading, quote, list or fence only parses at the start of a line, so it
// moves below the "Now:" prefix instead of being swallowed into its paragraph.
const LEADING_MARKDOWN_BLOCK = /^(#{1,6}\s|>|[-*+]\s|\d+[.)]\s|```|~~~|\|)/;
const separateLeadingBlock = (activity: string) =>
  LEADING_MARKDOWN_BLOCK.test(activity) ? `\n\n${activity}` : activity;

/**
 * Body of a RUNNING computer-use card: either the queued notice (the
 * target's assignment slot is held by a different run — this task waits at
 * the resource gate and starts automatically when the holder finishes) or
 * the live view + step ladder once this run owns the target.
 *
 * Queued detection needs both a child run id (to tell "queued behind
 * another run" apart from "our own run booting") and a React Query client
 * (absent in some standalone hosts and unit tests) — without either we
 * render the plain live body.
 */
export const ComputerUseRunningBody = (props: RunningBodyProps): JSX.Element => {
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  if (!hasQueryClient || !props.childAgentRunId) return <LiveBody {...props} />;
  return <QueueAwareBody {...props} />;
};

const QueueAwareBody = (props: RunningBodyProps): JSX.Element => {
  const intl = useIntl();
  const [stoppingOccupying, setStoppingOccupying] = useState(false);

  // Queued detection only matters before any step lands — once steps flow,
  // this run holds the target and the poll stops.
  const queuedBehindTarget = props.runControl.useQueuedTarget({
    childAgentRunId: props.childAgentRunId,
    enabled: (props.trace?.length ?? 0) === 0 && !props.awaitingApproval,
  });
  const occupyingRunId = queuedBehindTarget?.busyAgentRunId;
  const occupyingProgress = useSubAgentComputerUseProgress(
    occupyingRunId,
    agentHarnessClient,
    { streamHeaders }
  );
  const occupyingRunQuery = useQueryRun(occupyingRunId);

  if (!queuedBehindTarget) return <LiveBody {...props} />;

  const occupyingGoal = occupyingRunGoal(occupyingRunQuery.data);
  const occupyingActivity = occupyingProgress.childRunApproval
    ? intl.formatMessage({
        defaultMessage: 'Waiting for a decision',
        description:
          'Queued computer-use card: the occupying run is paused on an approval',
        id: 'IiWS2DZhF1',
      })
    : occupyingProgress.liveStep?.trim() || occupyingGoal;

  // The binding refreshes the desktop-target roster with the stop.
  const onStopCurrentAndStartThis = () => {
    if (!occupyingRunId || stoppingOccupying) return;
    setStoppingOccupying(true);
    props.runControl.cancelRun(occupyingRunId);
  };

  return (
    <div
      className="flex flex-col gap-2.5 rounded-lg border border-border bg-background-tertiary/60 p-3"
      data-testid="computer-use-queued-banner"
    >
      <div className="flex items-start gap-2.5">
        <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin text-text-secondary" />
        <div className="flex min-w-0 flex-col gap-1">
          <TypographyCaption className="text-text-secondary">
            {intl.formatMessage(
              {
                defaultMessage:
                  '{name} is busy with another task. This task is queued and will start automatically when it finishes.',
                id: 'Sd2GiAXIyD',
              },
              { name: queuedBehindTarget.displayName }
            )}
          </TypographyCaption>
          {occupyingActivity ? (
            <ComputerUseMarkdown
              className="text-text-primary"
              data-testid="computer-use-queued-occupying-activity"
            >
              {intl.formatMessage(
                {
                  defaultMessage: 'Now: {activity}',
                  description:
                    'Queued computer-use card: the occupying run’s current step or goal',
                  id: 'FH+KxbsoVL',
                },
                { activity: separateLeadingBlock(occupyingActivity) }
              )}
            </ComputerUseMarkdown>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        className="w-fit gap-1.5"
        onClick={onStopCurrentAndStartThis}
        disabled={stoppingOccupying || !occupyingRunId}
        data-testid="computer-use-queued-steal"
      >
        {stoppingOccupying ? (
          <>
            <Loader2 className="size-3 animate-spin" />
            {intl.formatMessage({
              defaultMessage: 'Stopping current task…',
              description:
                'Queued computer-use card: cancel of the occupying run is in flight',
              id: 't4CFbTrm1z',
            })}
          </>
        ) : (
          <>
            <Square className="size-3 fill-current" />
            {intl.formatMessage({
              defaultMessage: 'Stop current task and start this one',
              description:
                'Queued computer-use card: cancel the occupying run so this queued task can start',
              id: 'SMgHE39tEp',
            })}
          </>
        )}
      </Button>
    </div>
  );
};

const LiveBody = ({
  childAgentRunId,
  trace,
  elapsedSeconds,
  hideLiveView,
  renderLiveView,
}: RunningBodyProps): JSX.Element => (
  <>
    {!hideLiveView && renderLiveView(childAgentRunId)}
    <ComputerUseWorkingBlock trace={trace} running elapsedSeconds={elapsedSeconds} />
  </>
);

const occupyingRunGoal = (run: PublicRunSnapshot | undefined): string | undefined => {
  const title = run?.title?.trim();
  if (title) return truncateGoal(title);
  const messages = run?.messages ?? [];
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role !== 'user') continue;
    const text = message.blocks.find(
      (block) => block.kind === 'text' && block.text.trim()
    );
    if (text?.kind === 'text') return truncateGoal(text.text.trim());
  }
  return undefined;
};

const truncateGoal = (text: string): string =>
  text.length <= OCCUPYING_GOAL_MAX_CHARS
    ? text
    : `${text.slice(0, OCCUPYING_GOAL_MAX_CHARS - 1)}…`;
